import { PublicAccountSelect } from '@components'
import { Checkbox, Input, Modal, ModalFooter, ModalHeader } from '@components-deprecated'

import { Button, Text } from '@billy/design-system'
import { yupResolver } from '@hookform/resolvers/yup'
import isEmpty from 'lodash/isEmpty'
import React, { useCallback, useEffect } from 'react'
import { ErrorOption, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import { SaveAccountPayload, updateAccount, UpdateAccountResponse } from '@features/accounts/query-api'
import { EmberSaveAccountProps } from '@features/chartOfAccounts/types/emberSaveAccountProps'
import { AccountGroupSelector } from '@modules-deprecated/app/accounts/elements'
import { Account, AccountGroup } from '@modules-deprecated/app/accounts/types'
import { BankSelector } from '@modules-deprecated/app/banks/elements'
import { CurrencySelector } from '@modules-deprecated/app/currencies/elements'
import { Currency } from '@modules-deprecated/app/currencies/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { ChartOfAccountId } from '@modules-deprecated/app/organization/enums/chartOfAccountId'
import { UiSetup } from '@modules-deprecated/app/organization/enums/uiSetup'
import { TaxRateSelector } from '@modules-deprecated/app/taxrates/elements'

import { APIError } from '../../../../../../utils'
import { saveAccount } from '../../query-api'
import { submitFailureCallback } from '../../utils/submitFailureCallback'
import { EditAccountSelectWrapper } from './elements/EditAccountSelectWrapper'
import * as Styled from './styles'
import { AddAccountFormInputs, AllowedField } from './types/types'
import {
  filterSaveAccountPayload,
  getSaveAccountInitialData,
  getSaveAccountPayload,
  getValidationSchema,
} from './utils'

interface EditAccountModalProps {
  account?: Account
  group?: AccountGroup
  isOpen?: boolean
  onClose?: () => void
  onSave?: (response?: EmberSaveAccountProps) => void
  allowedFields?: AllowedField[]
}

export const EditAccountModal = ({
  isOpen = false,
  onClose = () => null,
  onSave = () => null,
  account,
  group,
  allowedFields,
  ...rest
}: EditAccountModalProps) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const isExternalManagedBankAccount = Boolean(account && account.isBankAccount && account?.externalId)

  const form = useForm<AddAccountFormInputs>({
    defaultValues: getSaveAccountInitialData(account, group?.id),
    resolver: yupResolver(getValidationSchema(allowedFields)),
  })

  const { setError } = form

  const submitSuccessCallback = (data?: UpdateAccountResponse) => {
    const { bankSwift, bankIban } = form.getValues()
    onSave({
      bankSwift,
      bankIban,
      account: data?.accounts[0],
    })
    onClose()
  }

  const onSaveOrUpdateErrorCallback = useCallback(
    (attributeName?: keyof SaveAccountPayload, errorMessage?: string) => {
      if (!attributeName) {
        return
      }

      const errorOption: ErrorOption = { message: errorMessage || '' }
      if (attributeName === 'accountNo') {
        setError('accountNo', errorOption)
      } else if (attributeName === 'name') {
        setError('accountName', errorOption)
      }
    },
    [setError],
  )

  const updateAccountMutation = useMutation((payload: Partial<SaveAccountPayload>) => updateAccount(payload), {
    onSuccess: submitSuccessCallback,
    onError: (error: APIError) => {
      submitFailureCallback(error, t, onSaveOrUpdateErrorCallback)
    },
  })

  const saveAccountMutation = useMutation((payload: SaveAccountPayload) => saveAccount(payload), {
    onSuccess: submitSuccessCallback,
    onError: (error: APIError) => {
      submitFailureCallback(error, t, onSaveOrUpdateErrorCallback)
    },
  })

  const { isBankAccount, isPaymentEnabled, currency } = form.watch()
  const selectedAccountGroup = form.watch('accountGroup')
  const shouldShowPaymentControls =
    organization?.chartOfAccountId === ChartOfAccountId.Manual
      ? selectedAccountGroup?.natureId === 'liability' ||
        selectedAccountGroup?.natureId === 'asset' ||
        group?.natureId === 'liability' ||
        group?.natureId === 'asset' ||
        account?.isPaymentEnabled
      : selectedAccountGroup?.allowPaymentAmounts || group?.allowPaymentAmounts // either selected account group has payments turned on
  const showBankInformation = isBankAccount && organization?.uiSetup === UiSetup.Accounting

  const isFieldDisabled = useCallback(
    (fieldName: AllowedField) => {
      if (!allowedFields || isEmpty(allowedFields)) {
        return false
      }

      return !allowedFields.includes(fieldName)
    },
    [allowedFields],
  )

  useEffect(() => {
    form.setValue('publicAccountId', undefined)
  }, [form, selectedAccountGroup?.natureId])

  useEffect(
    () => {
      if (isBankAccount && !currency?.id && organization) {
        form.setValue('currency', { id: organization.baseCurrencyId } as Currency)
      }
    },
    // Warning disabled during the eslint warning cleanup. When refactoring this code fix this properly if possible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBankAccount, organization],
  )

  useEffect(
    () => {
      form.reset(getSaveAccountInitialData(account, group?.id))
    },
    // Warning disabled during the eslint warning cleanup. When refactoring this code fix this properly if possible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, isOpen],
  )

  const submit = () => {
    form.handleSubmit((data: AddAccountFormInputs) => {
      const payload = getSaveAccountPayload(data, organization?.id, account?.id)
      const payloadFiltered = filterSaveAccountPayload(payload, allowedFields)

      if (account) {
        updateAccountMutation.mutate(payloadFiltered)
      } else {
        saveAccountMutation.mutate(payloadFiltered)
      }
    })()
  }

  return (
    <Modal isOpen={isOpen} {...rest} onClose={onClose} variant="wide" width={600}>
      <ModalHeader heading={account ? t('add_account_modal.title_edit') : t('add_account_modal.title_create') || ''} />
      <Styled.ModalBodyScrollable>
        <Text variant="h2">{t('add_account_modal.basic_information')}</Text>
        <FormProvider {...form}>
          <Styled.FormSection>
            <Styled.GridContainer>
              <AccountGroupSelector
                disabled={isFieldDisabled('accountGroup') || isExternalManagedBankAccount}
                formControl={form.control}
                label={t('add_account_modal.account_type')}
                name="accountGroup"
                required
                silent
              />
              <Styled.TextContainer>
                <Text variant="bodySM" colorVariant="secondary">
                  {t('add_account_modal.type.description')}
                </Text>
              </Styled.TextContainer>
              <Input
                disabled={isFieldDisabled('accountNo') || isExternalManagedBankAccount}
                formControl={form.control}
                label={t('add_account_modal.account_number')}
                name="accountNo"
                required
                silent
              />
              <Styled.TextContainer>
                <Text variant="bodySM" colorVariant="secondary">
                  {t('add_account_modal.account_number.description')}
                </Text>
              </Styled.TextContainer>
              <Input
                disabled={isFieldDisabled('accountName') || isExternalManagedBankAccount}
                formControl={form.control}
                name="accountName"
                label={t('add_account_modal.account_name')}
                required
                silent
              />
              <Styled.EmptyCell />

              {shouldShowPaymentControls && (
                <>
                  <Checkbox
                    disabled={isFieldDisabled('isBankAccount') || !!account}
                    formControl={form.control}
                    label={t('add_account_modal.bank_account')}
                    name="isBankAccount"
                  />
                  <Styled.EmptyCell />
                  <Checkbox
                    disabled={isFieldDisabled('isPaymentEnabled')}
                    formControl={form.control}
                    label={t('add_account_modal.enable_payments')}
                    name="isPaymentEnabled"
                  />
                  <Styled.EmptyCell />
                </>
              )}

              <Input
                disabled={isFieldDisabled('description') || isExternalManagedBankAccount}
                formControl={form.control}
                label={t('add_account_modal.description')}
                name="description"
                silent
                type="textarea"
              />
              <Styled.TextContainer>
                <Text variant="bodySM" colorVariant="secondary">
                  {t('add_account_modal.description.description')}
                </Text>
              </Styled.TextContainer>

              <TaxRateSelector
                disabled={
                  isBankAccount || isPaymentEnabled || isFieldDisabled('vatRate') || isExternalManagedBankAccount
                }
                formControl={form.control}
                label={t('add_account_modal.vat_rate')}
                name="vatRate"
                placeholder=""
                preselectedId={account?.taxRateId}
                organizationId={organization?.id}
                silent
              />
              <Styled.TextContainer>
                <Text variant="bodySM" colorVariant="secondary">
                  {t('add_account_modal.vat_rate.description')}
                </Text>
              </Styled.TextContainer>

              <EditAccountSelectWrapper
                formControl={form.control}
                name="publicAccountId"
                label={t('add_account_modal.public_account')}
                render={(value, onSelect) => {
                  return (
                    <PublicAccountSelect
                      onSelect={onSelect}
                      selectedId={value}
                      natureId={selectedAccountGroup?.natureId}
                      disabled={isFieldDisabled('publicAccountId') || isExternalManagedBankAccount}
                    />
                  )
                }}
              />

              <Styled.TextContainer>
                <Text variant="bodySM" colorVariant="secondary">
                  {t('add_account_modal.public_account.description')}{' '}
                </Text>
              </Styled.TextContainer>
              {isBankAccount && (
                <>
                  <CurrencySelector
                    disabled={isFieldDisabled('currency') || isExternalManagedBankAccount}
                    formControl={form.control}
                    label={t('add_account_modal.currency')}
                    name="currency"
                    required
                    silent
                  />
                  <Styled.EmptyCell />
                </>
              )}
            </Styled.GridContainer>
          </Styled.FormSection>

          {showBankInformation && (
            <Styled.FormSection>
              <Text variant="h2">{t('add_account_modal.bank_information')}</Text>
              <Styled.GridContainer>
                <BankSelector
                  countryId="DK"
                  disabled={isFieldDisabled('bank') || isExternalManagedBankAccount}
                  formControl={form.control}
                  label={t('add_account_modal.bank')}
                  name="bank"
                />
                <Styled.EmptyCell />

                <Input
                  disabled={isFieldDisabled('routingNumber') || isExternalManagedBankAccount}
                  formControl={form.control}
                  label={t('add_account_modal.routing_number')}
                  name="routingNumber"
                />
                <Styled.EmptyCell />

                <Input
                  disabled={isFieldDisabled('bankAccountNo') || isExternalManagedBankAccount}
                  formControl={form.control}
                  label={t('add_account_modal.bank_account_number')}
                  name="bankAccountNo"
                  type="number"
                />
                <Styled.EmptyCell />

                <Input
                  disabled={isFieldDisabled('bankSwift') || isExternalManagedBankAccount}
                  formControl={form.control}
                  label={t('add_account_modal.swift')}
                  name="bankSwift"
                />
                <Styled.EmptyCell />

                <Input
                  disabled={isFieldDisabled('bankIban') || isExternalManagedBankAccount}
                  formControl={form.control}
                  label={t('add_account_modal.iban')}
                  name="bankIban"
                />
                <Styled.EmptyCell />
              </Styled.GridContainer>
            </Styled.FormSection>
          )}
        </FormProvider>
      </Styled.ModalBodyScrollable>

      <ModalFooter>
        <Button variant="text" onClick={onClose}>
          {t('add_account_modal.cancel')}
        </Button>
        <Button variant="filled" onClick={submit}>
          {t('add_account_modal.save_account')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
